import { motion } from "framer-motion";
import { ArrowLeft, Phone, Mail, MapPin, Clock, Shield, Award, Wrench, Droplet, Flame, AlertTriangle, CheckCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ProjectTopBar from "@/components/ProjectTopBar";
import Seo from "@/components/Seo";

export default function PlombierPro() {
  const navigate = useNavigate(); const setLocation = (p) => navigate(p);
  const [currentPage, setCurrentPage] = useState("accueil");

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      icon: <Droplet className="w-12 h-12" />,
      name: "Dépannage Plomberie",
      desc: "Réparation de fuites, remplacement de robinetterie, débouchage de canalisations",
      price: "À partir de 60€",
      image: "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: <Flame className="w-12 h-12" />,
      name: "Chauffage & Chaudière",
      desc: "Installation, maintenance et dépannage de systèmes de chauffage",
      price: "À partir de 85€",
      image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=400&fit=crop&q=80"
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      name: "Installation Sanitaire",
      desc: "Pose de baignoires, douches, WC, lavabos et équipements",
      price: "À partir de 75€",
      image: "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=600&h=400&fit=crop&q=80"
    },
  ];

  return (
    <>
      <Seo title="Dépann'Eau Pro — concept plomberie" description="Exemple de site pour un artisan plombier : services, urgences et devis. Concept ML. Agence Web." />
      <ProjectTopBar />
      <div className="min-h-screen bg-white">
      {/* Emergency Banner */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 py-3 text-white text-center font-bold text-lg">
        🚨 URGENCE 24/7 : <a href="tel:0102032526" className="text-2xl hover:underline">01 02 03 25 26</a> • Intervention en moins de 2h
      </div>

      {/* Navigation */}
      <nav className="bg-[#1e3a8a] text-white sticky top-0 z-40 shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black">DÉPANN'EAU PRO</h1>
                <p className="text-sm text-blue-200">Votre expert en plomberie et chauffage</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-6">
              {[
                { id: "accueil", label: "ACCUEIL" },
                { id: "services", label: "SERVICES" },
                { id: "urgences", label: "URGENCES" },
                { id: "devis", label: "DEVIS" },
                { id: "zone", label: "ZONE" },
                { id: "certifications", label: "CERTIFICATIONS" },
                { id: "contact", label: "CONTACT" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => handlePageChange(item.id )}
                  className={`text-sm font-bold tracking-wider transition-all pb-1 border-b-2 ${
                    currentPage === item.id 
                      ? "text-white border-orange-500" 
                      : "text-blue-200 border-transparent hover:text-white hover:border-blue-400"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* ACCUEIL PAGE */}
      {currentPage === "accueil" && (
        <>
          {/* Hero */}
          <section className="grid md:grid-cols-[70%_30%]">
            <div className="relative h-[440px] md:h-[600px]">
              <img
                src="https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1200&h=800&fit=crop&q=80" 
                alt="Plombier professionnel"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-transparent flex items-center">
                <div className="max-w-2xl px-6 md:px-12">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h1 className="text-4xl md:text-6xl font-black text-white mb-4">Dépann'Eau Pro</h1>
                    <p className="text-2xl text-white/90 mb-8">Votre expert en plomberie et chauffage</p>
                    <Button 
                      size="lg" 
                      className="bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold px-10 h-16"
                      onClick={() => handlePageChange("devis")}
                    >
                      Demander un devis gratuit
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-red-600 p-8 md:p-12 flex flex-col justify-center items-center text-white">
              <Phone className="w-16 h-16 md:w-20 md:h-20 mb-4" />
              <p className="text-xl font-bold mb-2">URGENCE 24/7</p>
              <a href="tel:0102032526" className="text-4xl md:text-6xl font-black mb-6 hover:scale-105 transition-transform">01 02 03 25 26</a>
              <p className="text-center">
                <strong>Intervention rapide</strong><br />
                Devis gratuit
              </p>
            </div>
          </section>

          {/* Badges */}
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-wrap justify-center gap-8">
                {[
                  { icon: <Shield className="w-8 h-8" />, text: "Garantie décennale" },
                  { icon: <Award className="w-8 h-8" />, text: "Certifié Qualibat" },
                  { icon: <Users className="w-8 h-8" />, text: "+15 ans d'expérience" },
                ].map((badge, i) => (
                  <div key={i} className="flex items-center gap-3 px-8 py-4 bg-white rounded-xl border-2 border-blue-200 shadow-md">
                    <div className="text-blue-900">{badge.icon}</div>
                    <span className="text-blue-900 font-bold text-lg">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Why Us */}
          <section className="py-20 px-6 bg-white">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-5xl font-black text-blue-900 text-center mb-16">Pourquoi nous choisir ?</h2>
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { title: "Réactivité", desc: "Intervention sous 2h en urgence", icon: "⚡" },
                  { title: "Expertise", desc: "Équipe qualifiée et formée", icon: "🎓" },
                  { title: "Tarifs clairs", desc: "Devis détaillé gratuit", icon: "💰" },
                  { title: "Garantie", desc: "Travaux garantis 2 ans", icon: "✅" },
                ].map((item, i) => (
                  <Card key={i} className="p-8 text-center border-2 border-blue-100 hover:border-blue-900 hover:shadow-xl transition-all bg-white">
                    <div className="text-4xl md:text-6xl mb-4">{item.icon}</div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Services Preview */}
          <section className="py-20 px-6 bg-gray-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-5xl font-black text-blue-900 text-center mb-16">Nos Services</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, i) => (
                  <Card key={i} className="overflow-hidden border-2 border-blue-100 hover:border-blue-900 hover:shadow-2xl transition-all group bg-white">
                    <div className="aspect-video relative overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center text-blue-900 mb-4 group-hover:bg-blue-900 group-hover:text-white transition-colors">
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-blue-900 mb-3">{service.name}</h3>
                      <p className="text-gray-600 mb-4">{service.desc}</p>
                      <p className="text-2xl font-bold text-orange-500">{service.price}</p>
                    </div>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-12">
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold px-12 h-16"
                  onClick={() => handlePageChange("services")}
                >
                  Voir tous nos services
                </Button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* SERVICES PAGE */}
      {currentPage === "services" && (
        <div className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-black text-blue-900 text-center mb-16">Nos Services</h2>
            <div className="space-y-16">
              {services.map((service, i) => (
                <div key={i} className={`grid md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={i % 2 === 1 ? 'md:col-start-2' : ''}>
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
                    />
                  </div>
                  <div className={i % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}>
                    <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-900 mb-6">
                      {service.icon}
                    </div>
                    <h3 className="text-4xl font-bold text-blue-900 mb-4">{service.name}</h3>
                    <p className="text-xl text-gray-600 mb-6 leading-relaxed">{service.desc}</p>
                    <p className="text-3xl font-bold text-orange-500 mb-6">{service.price}</p>
                    <Button 
                      className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-6 text-lg"
                      onClick={() => handlePageChange("devis")}
                    >
                      Demander un devis
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* URGENCES PAGE */}
      {currentPage === "urgences" && (
        <div>
          <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-12 text-center">
            <p className="text-4xl font-black mb-4">🚨 URGENCE 24/7 🚨</p>
            <p className="text-2xl">Intervention rapide partout en Île-de-France</p>
          </div>
          
          <div className="max-w-6xl mx-auto px-6 py-20">
            <a href="tel:0102032526" className="block mb-16">
              <Card className="p-8 md:p-16 bg-gradient-to-br from-orange-500 to-red-600 hover:opacity-90 transition-opacity text-center cursor-pointer">
                <Phone className="w-16 h-16 md:w-24 md:h-24 text-white mx-auto mb-6" />
                <p className="text-3xl md:text-8xl font-black text-white mb-6">01 02 03 25 26</p>
                <p className="text-lg md:text-3xl text-white font-bold">Cliquez pour appeler maintenant</p>
              </Card>
            </a>

            <h2 className="text-4xl font-black text-blue-900 text-center mb-12">Types d'urgences traitées</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {[
                { 
                  title: "Fuite d'eau", 
                  icon: <Droplet className="w-16 h-16 text-blue-600" />,
                  image: "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=400&h=300&fit=crop&q=80"
                },
                { 
                  title: "Dégât des eaux", 
                  icon: <AlertTriangle className="w-16 h-16 text-red-600" />,
                  image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&q=80"
                },
                { 
                  title: "WC bouchés", 
                  icon: <Wrench className="w-16 h-16 text-orange-600" />,
                  image: "https://images.unsplash.com/photo-1620626011761-996317b8d101?w=400&h=300&fit=crop&q=80"
                },
                { 
                  title: "Chaudière en panne", 
                  icon: <Flame className="w-16 h-16 text-red-600" />,
                  image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&h=300&fit=crop&q=80"
                },
              ].map((urgence, i) => (
                <Card key={i} className="overflow-hidden border-2 border-blue-900 hover:shadow-2xl transition-all bg-white">
                  <img 
                    src={urgence.image} 
                    alt={urgence.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6 text-center bg-white">
                    <div className="flex justify-center mb-4">{urgence.icon}</div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-4">{urgence.title}</h3>
                    <Button className="bg-red-600 hover:bg-red-700 text-white w-full py-6 text-lg font-bold">
                      Appeler maintenant
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            <Card className="p-12 bg-blue-50 border-2 border-blue-900">
              <h3 className="text-3xl font-bold text-blue-900 mb-8 text-center">Notre processus d'intervention</h3>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  { step: "1", title: "Appel", desc: "Vous nous contactez" },
                  { step: "2", title: "Diagnostic", desc: "Évaluation téléphonique" },
                  { step: "3", title: "Intervention", desc: "Arrivée sous 2h" },
                  { step: "4", title: "Réparation", desc: "Dépannage efficace" },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center text-3xl font-black mx-auto mb-4 shadow-lg">
                      {item.step}
                    </div>
                    <h4 className="font-bold text-blue-900 text-xl mb-2">{item.title}</h4>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </Card>

            <div className="text-center mt-12 p-8 bg-green-50 border-4 border-green-500 rounded-2xl">
              <p className="text-2xl font-black text-green-700">⏱️ Garantie temps de réponse : Arrivée sous 2h maximum</p>
            </div>
          </div>
        </div>
      )}

      {/* DEVIS PAGE */}
      {currentPage === "devis" && (
        <div className="py-20 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-5xl font-black text-blue-900 text-center mb-4">Demande de devis en ligne</h2>
            <p className="text-center text-gray-600 mb-16 text-xl">Gratuit et sans engagement</p>
            
            <div className="grid md:grid-cols-[70%_30%] gap-8">
              <Card className="p-10 border-2 border-blue-900 bg-white">
                <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-4">Type d'intervention</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {['Dépannage', 'Installation', 'Rénovation', 'Entretien'].map(type => (
                        <label key={type} className="flex items-center gap-3 p-4 border-2 border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors bg-white">
                          <input type="radio" name="type" className="w-5 h-5" />
                          <span className="text-lg font-medium text-gray-900">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-blue-900 font-bold mb-2 text-lg">Description du problème</label>
                    <Textarea 
                      rows={5} 
                      className="border-2 border-blue-200 focus:border-blue-500 bg-white text-gray-900"
                      placeholder="Décrivez votre besoin en détail..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-blue-900 font-bold mb-2">Nom</label>
                      <Input className="border-2 border-blue-200 focus:border-blue-500 bg-white text-gray-900" />
                    </div>
                    <div>
                      <label className="block text-blue-900 font-bold mb-2">Prénom</label>
                      <Input className="border-2 border-blue-200 focus:border-blue-500 bg-white text-gray-900" />
                    </div>
                    <div>
                      <label className="block text-blue-900 font-bold mb-2">Téléphone</label>
                      <Input type="tel" className="border-2 border-blue-200 focus:border-blue-500 bg-white text-gray-900" />
                    </div>
                    <div>
                      <label className="block text-blue-900 font-bold mb-2">Email</label>
                      <Input type="email" className="border-2 border-blue-200 focus:border-blue-500 bg-white text-gray-900" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-blue-900 font-bold mb-2">Adresse complète</label>
                    <Input className="border-2 border-blue-200 focus:border-blue-500 bg-white text-gray-900" />
                  </div>

                  <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-xl font-bold">
                    Envoyer ma demande
                  </Button>
                </form>
              </Card>

              <div>
                <Card className="p-8 bg-blue-50 border-2 border-blue-900 sticky top-24">
                  <h3 className="text-xl font-bold text-blue-900 mb-6">Pourquoi demander un devis ?</h3>
                  <ul className="space-y-4">
                    {[
                      'Gratuit et sans engagement',
                      'Réponse sous 24h',
                      'Tarifs détaillés',
                      'Conseils personnalisés',
                      'Planning adapté',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ZONE PAGE */}
      {currentPage === "zone" && (
        <div className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-5xl font-black text-blue-900 text-center mb-16">Zone d'intervention</h2>
            
            <div className="grid md:grid-cols-[70%_30%] gap-8 mb-12">
              <div className="aspect-video rounded-2xl overflow-hidden border-2 border-blue-900">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d332892.2!2d2.3486!3d48.8534!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1f06e2b005%3A0x40b82c3688c9460!2sParis!5e0!3m2!1sfr!2sfr!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>

              <div>
                <Card className="p-6 border-2 border-blue-900 mb-6 bg-white">
                  <h3 className="text-xl font-bold text-blue-900 mb-4">Villes couvertes</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {[
                      'Paris (75)',
                      'Boulogne (92)',
                      'Versailles (78)',
                      'Créteil (94)',
                      'Nanterre (92)',
                      'Saint-Denis (93)',
                      'Argenteuil (95)',
                      'Montreuil (93)',
                    ].map((ville, i) => (
                      <div key={i} className="p-3 bg-blue-50 rounded-lg border border-blue-200 font-medium text-gray-700">
                        {ville}
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6 bg-orange-50 border-2 border-orange-500">
                  <h3 className="text-lg font-bold text-orange-700 mb-4">Tarifs de déplacement</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Paris intra-muros:</span>
                      <span className="font-bold text-orange-700 text-xl">Gratuit</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Petite couronne:</span>
                      <span className="font-bold text-orange-700 text-xl">15€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Grande couronne:</span>
                      <span className="font-bold text-orange-700 text-xl">25€</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CERTIFICATIONS PAGE */}
      {currentPage === "certifications" && (
        <div className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-black text-blue-900 text-center mb-16">Nos Certifications</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {[
                { name: "Qualibat", number: "RGE 2024-001234", desc: "Certification qualité bâtiment" },
                { name: "Garantie Décennale", number: "GD-75-2024-5678", desc: "Assurance responsabilité civile" },
                { name: "QualiPAC", number: "QP-2024-9012", desc: "Pompes à chaleur certifiées" },
                { name: "PG Gaz", number: "PG-IDF-3456", desc: "Professionnel du gaz" },
              ].map((cert, i) => (
                <Card key={i} className="p-10 border-2 border-blue-900 text-center hover:shadow-2xl transition-all bg-white">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-900 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <Award className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-blue-900 mb-3">{cert.name}</h3>
                  <p className="text-xl font-mono text-gray-600 mb-4">{cert.number}</p>
                  <p className="text-gray-600 text-lg">{cert.desc}</p>
                </Card>
              ))}
            </div>

            <Card className="p-12 bg-green-50 border-4 border-green-500 mb-16">
              <h3 className="text-3xl font-bold text-green-700 mb-8 text-center">Nos Garanties</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  'Travaux garantis 2 ans',
                  'Devis gratuit et détaillé',
                  "Pièces d'origine",
                  'Intervention rapide',
                  'Tarifs transparents',
                  'SAV réactif',
                ].map((garantie, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-black text-xl flex-shrink-0">
                      ✓
                    </div>
                    <span className="text-gray-700 font-medium text-lg">{garantie}</span>
                  </div>
                ))}
              </div>
            </Card>

            <div>
              <h3 className="text-4xl font-bold text-blue-900 mb-12 text-center">Notre Équipe</h3>
              <div className="grid md:grid-cols-4 gap-6">
                {['Jean', 'Marc', 'Sophie', 'Thomas'].map((name, i) => (
                  <Card key={i} className="overflow-hidden border-2 border-blue-200 hover:border-blue-900 hover:shadow-xl transition-all bg-white">
                    <img 
                      src={`https://images.unsplash.com/photo-${['1568602471122-7832951cc4c5', '1566492031773-4f4e44671857', '1580489944761-15a19d654956', '1507003211169-0a1dd7228f2d'][i]}?w=300&h=300&fit=crop&crop=faces&q=80`}
                      alt={name}
                      className="w-full h-48 object-cover object-[50%_30%]"
                    />
                    <div className="p-6 text-center bg-blue-50">
                      <p className="font-black text-blue-900 text-xl mb-1">{name}</p>
                      <p className="text-gray-600 font-medium">Plombier qualifié</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CONTACT PAGE */}
      {currentPage === "contact" && (
        <div className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-black text-blue-900 text-center mb-16">Contactez-nous</h2>
            
            <div className="space-y-8 mb-12">
              <div className="aspect-[21/9] rounded-2xl overflow-hidden border-2 border-blue-900">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.4!2d2.3088!3d48.8566!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1f06e2b005%3A0x40b82c3688c9460!2s15%20Rue%20de%20la%20Plomberie%2C%2075015%20Paris!5e0!3m2!1sfr!2sfr!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <Card className="p-10 border-2 border-blue-900 bg-white">
                <h3 className="text-3xl font-bold text-blue-900 mb-8">Nos coordonnées</h3>
                <div className="space-y-6">
                  {[
                    { icon: <Phone className="w-8 h-8" />, title: "Téléphone", text: "01 02 03 25 26" },
                    { icon: <Mail className="w-8 h-8" />, title: "Email", text: "contact@depanneaupro.fr" },
                    { icon: <MapPin className="w-8 h-8" />, title: "Adresse", text: "15 Rue de la Plomberie\n75015 Paris" },
                    { icon: <Clock className="w-8 h-8" />, title: "Horaires", text: "Lun-Ven: 8h-18h\nSam: 9h-12h\nUrgences 24/7" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="text-blue-900 flex-shrink-0">{item.icon}</div>
                      <div>
                        <p className="font-bold text-blue-900 text-xl mb-1">{item.title}</p>
                        <p className="text-gray-600 text-lg whitespace-pre-line">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-10 border-2 border-blue-900 bg-white">
                <h3 className="text-3xl font-bold text-blue-900 mb-8">Envoyez-nous un message</h3>
                <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                  <Input placeholder="Nom" className="border-2 border-blue-200 focus:border-blue-500 py-6 text-lg bg-white text-gray-900" />
                  <Input type="email" placeholder="Email" className="border-2 border-blue-200 focus:border-blue-500 py-6 text-lg bg-white text-gray-900" />
                  <Textarea rows={4} placeholder="Votre message" className="border-2 border-blue-200 focus:border-blue-500 text-lg bg-white text-gray-900" />
                  <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-xl font-bold">
                    Envoyer
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* CTA Final */}
      <section className="py-32 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-7xl font-black mb-8">
            Un Problème de Plomberie ?
          </h2>
          <p className="text-lg md:text-3xl text-blue-100 mb-12">
            Intervention rapide • Devis gratuit • Travail garanti
          </p>
          <a href="tel:0102032526">
            <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 text-white text-lg md:text-3xl font-black px-6 md:px-20 h-16 md:h-24 rounded-full hover:scale-105 transition-all shadow-2xl max-w-full">
              <Phone className="w-6 h-6 md:w-10 md:h-10 mr-3 md:mr-4 shrink-0" />
              01 02 03 25 26
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#1e3a8a] text-white text-center">
        <p className="text-lg">© 2024 Dépann'Eau Pro - Plomberie & Chauffage. Made with 🔧 by ML</p>
        <p className="text-sm text-blue-200 mt-2">RCS Paris 123 456 789</p>
      </footer>
    </div>
    </>
  );
}
